import React, { useState, useEffect, useRef, useId } from "react";
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import shortid from 'shortid';
import { useTranslation } from 'react-i18next';
import MultiSelect from './MultiSelect';
import SearchCombobox from 'reusable/RemoteSearch/SearchCombobox';
import { FieldSearch } from './Edit';
import BirthdayInput from 'reusable/BirthdayInput';

export interface Field {
    type: string;
    label: string;
    options?: { name: string; id: string }[];
    /** EV-493。候補をサーバーで検索する（複数選択の欄）。編集の画面（Edit.tsx）と同じ。 */
    search?: FieldSearch;
    required?: boolean;
    autoGenerate?: boolean;
    /** EV-318。項目の下に出す説明（候補を読めなかったことなど）。編集の画面（Edit.tsx）と同じ。 */
    note?: string;
    /** EV-318。説明の横に出す操作（読み直しなど）。 */
    noteAction?: { label: string; onClick: () => void };
    /** I12-2。入力欄の例（文字の欄）。 */
    placeholder?: string;
}

interface AddNewProps {
    label: string;
    title: string;
    fields: Field[];
    save: string;
    onSave: (AddNewValue: { [key: string]: any }) => void;
    errorMessage?: string | null;
}

/**
 * EV-461。onSave がこれを投げたら、結果が分からないまま（前回の X-Request-ID を残した）。入力を送った値のまま固定し、
 * 「同じ内容で送り直す」か、やめるだけにする（入力を変えると新しい X-Request-ID になり、確定していた前回の登録を
 * 取り戻せず、二重に作ったり取り違えたりするため）。
 */
export class SaveUnresolvedError extends Error {
    readonly cause: unknown;
    constructor(cause: unknown) {
        super('Could not confirm the result of saving');
        this.name = 'SaveUnresolvedError';
        this.cause = cause;
    }
}

const generateUniqueId = () => {
    return shortid.generate().toUpperCase();
};

const AddNewPopUp: React.FC<AddNewProps> = ({ label, title, fields, save, onSave, errorMessage: externalErrorMessage }) => {
    const { t, i18n } = useTranslation('driver');
    const [showPopup, setShowPopup] = useState(false);
    const [addNewFormValue, setAddNewFormValue] = useState<{ [key: string]: any }>({});
    const [showOptions, setShowOptions] = useState<{ [key: string]: boolean }>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null); 
    let errorTimeout: NodeJS.Timeout | null = null;

    // 開いたときだけ初期化する。PR #99 レビュー。親は描画のたびに fields を作り直すため、
    // fields の変化で初期化すると、保存に失敗して親が再描画したときに入力が消えていた。
    useEffect(() => {
        if (showPopup) {
            const initialValues: { [key: string]: any } = {};
            fields.forEach(field => {
                if (field.autoGenerate) {
                    initialValues[field.label] = generateUniqueId();
                }
            });
            setAddNewFormValue(initialValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPopup]);

    // PR #99 レビュー。開いた（閉じた）回数。保存の結果は、その保存を始めたときに
    // 開いていた画面にだけ反映する（待ち中に閉じて開き直した画面を閉じたり、
    // 前の保存の失敗を出したりしない）。
    const session = useRef(0);
    // EV-461。送信中（savingRef）と、結果が分からないまま（unresolved）は入力を受け付けない。
    const savingRef = useRef(false);
    const [unresolved, setUnresolved] = useState(false);
    const unresolvedRef = useRef(false);
    const setLocked = (value: boolean) => {
        unresolvedRef.current = value;
        setUnresolved(value);
    };
    const editable = () => !savingRef.current && !unresolvedRef.current;

    const handleButtonClick = () => {
        session.current += 1;
        setLocked(false);
        setShowPopup(true);
    };

    const handleClosePopup = () => {
        // 送信中に閉じても、結果は開いていた画面にだけ反映する（PR #99 レビュー）。運転者の登録は送る前に
        // X-Request-ID と本文を残すので（Toolbar の registerDriverOnce）、開き直して同じ内容なら同じ値で送る。
        session.current += 1;
        savingRef.current = false;
        setIsSaving(false);
        setShowPopup(false);
        setErrorMessage(null); 
        clearTimeout(errorTimeout as NodeJS.Timeout); 
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (!editable()) return;
        const { name, value } = e.target;
        setAddNewFormValue({
            ...addNewFormValue,
            [name]: value,
        });
    };

    const handleMultiSelectChange = (e: React.ChangeEvent<HTMLInputElement>, label: string) => {
        if (!editable()) return;
        const { value, checked } = e.target;
        const parsedValue = JSON.parse(value);
        setAddNewFormValue(prevState => {
            const selectedOptions = prevState[label] || [];
            if (checked) {
                return { ...prevState, [label]: [...selectedOptions, parsedValue] };
            } else {
                return { ...prevState, [label]: selectedOptions.filter((option: any) => option.id !== parsedValue.id) };
            }
        });
    };

    const setOptionsOpen = (label: string, open: boolean) => {
        setShowOptions(prevState => ({
            ...prevState,
            [label]: open
        }));
    };

    // EV-493。検索の欄で候補を選んだ（選んでいれば何もしない）。
    const addOption = (label: string, option: any) => {
        if (!editable()) return;
        setAddNewFormValue(prevState => {
            const selectedOptions = prevState[label] || [];
            if (selectedOptions.some((item: any) => item.id === option.id)) return prevState;
            return { ...prevState, [label]: [...selectedOptions, { name: option.name, id: option.id }] };
        });
    };

    const removeOption = (label: string, option: any) => {
        if (!editable()) return;
        setAddNewFormValue(prevState => ({
            ...prevState,
            [label]: prevState[label].filter((item: any) => item.id !== option.id)
        }));
    };

    /**
     * ADM-15。保存の完了を待ってから閉じる。
     *
     * 以前は onSave の完了を待たずにダイアログを閉じ、入力内容も消していた。
     * 保存に失敗しても「登録できた」ように見え、入力し直しになっていた。
     */
    const [isSaving, setIsSaving] = useState(false);
    // ADM-16。ダイアログの名前（見出し）・Escape・フォーカスの閉じ込めと戻し。
    const titleId = useId();
    // ADM-16。項目名（label）と入力欄を結び付ける（読み上げで何の欄か分かるように）。
    const fieldId = useId();
    const dialogRef = useModalDialog(showPopup, handleClosePopup);
    const noteId = useId();
    const locked = isSaving || unresolved;

    const handleSaveChanges = async () => {
        const missingFields = fields.filter(field => field.required && !addNewFormValue[field.label]);
        if (missingFields.length > 0) {
            setErrorMessage(`${t('add-driver.add-driver-alert')} ${missingFields.map(field => field.label).join(", ")}`);

            if (errorTimeout) {
                clearTimeout(errorTimeout);
            }

            errorTimeout = setTimeout(() => {
                setErrorMessage(null);
            }, 3000);
            return;
        }

        if (savingRef.current) return;   // 連打で二重に登録しない
        savingRef.current = true;
        setIsSaving(true);
        const started = session.current;
        try {
            await onSave(addNewFormValue);
            if (started !== session.current) return;
            setLocked(false);
            setShowPopup(false);
            setErrorMessage(null);
            clearTimeout(errorTimeout as NodeJS.Timeout);
        } catch (error) {
            if (started !== session.current) return;
            // 入力内容は残したまま、失敗を伝える。EV-461。結果が分からないままなら、入力を送った値のまま固定する。
            const unresolvedNow = error instanceof SaveUnresolvedError;
            setLocked(unresolvedNow);
            // 結果が分からないときは「失敗した」とは言わない（理由は親の知らせ、固定の説明は下の文）。
            setErrorMessage(unresolvedNow ? null : t('add-driver.save-failed'));
        } finally {
            if (started === session.current) {
                savingRef.current = false;
                setIsSaving(false);
            }
        }
    };

    return (
        <section>
            <button type="button"
                className="py-2 px-4 bg-primary/300 text-white rounded-md"
                onClick={handleButtonClick}
            >
                {label}
            </button>

            {showPopup && inModalLayer(
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-50">
                    <div
                        className="bg-white rounded-md shadow-md w-full max-w-2xl relative"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        ref={dialogRef}
                        tabIndex={-1}
                    >
                        <button
                            type="button"
                            className="absolute top-2 right-4 text-gray-600 hover:text-gray-800 text-title"
                            onClick={handleClosePopup}
                            aria-label={t('ui.close', { ns: 'common' })}
                        >
                            &times;
                        </button>
                        <h2 id={titleId} className="text-subtitle-1 font-semi-bold px-6 py-4">{title}</h2>

                        {/* ADM-16。失敗の説明は読み上げで伝わるようにする。 */}
                        {errorMessage && (
                            <div className="px-6 py-2 text-red-700" role="alert">
                                {errorMessage}
                            </div>
                        )}

                        {/* EV-461。送信中と結果が分からない間は、入力をまとめて無効にする（送った値のまま）。 */}
                        <fieldset disabled={locked} className="border-y min-w-0" aria-describedby={unresolved ? noteId : undefined}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-y-0 px-6">
                                {fields.map((field, index) => (
                                    <div key={index} className="my-4">
                                        <label className="block mb-2" htmlFor={`${fieldId}-${index}`}>
                                            {field.label} {field.required && <span className="text-red-500">*</span>}
                                        </label>
                                        {field.type === "text" || field.type === "email" || field.type === "number" ? (
                                            <input
                                                type={field.type}
                                                name={field.label}
                                                id={`${fieldId}-${index}`}
                                                className="border p-2 rounded-md w-full"
                                                value={addNewFormValue[field.label] || ""}
                                                onChange={handleInputChange}
                                                readOnly={field.autoGenerate}
                                                placeholder={field.placeholder}
                                            />
                                        ) : field.type === "birthday" ? (
                                            // I12-1。生年月日は年・月・日の3つの選択（そろったときだけ「YYYY-MM-DD」）。
                                            <BirthdayInput
                                                id={`${fieldId}-${index}`}
                                                label={field.label}
                                                value={addNewFormValue[field.label] || ""}
                                                onChange={(value) => {
                                                    if (!editable()) return;
                                                    setAddNewFormValue((prev) => ({ ...prev, [field.label]: value }));
                                                }}
                                            />
                                        ) : field.type === "calendar" ? (
                                            <input
                                                type="date"
                                                name={field.label}
                                                id={`${fieldId}-${index}`}
                                                className="border p-2 rounded-md w-full"
                                                onChange={handleInputChange}
                                            />
                                        ) : field.type === "dropdown" ? (
                                            <select
                                                name={field.label}
                                                id={`${fieldId}-${index}`}
                                                className="border p-2 rounded-md w-full"
                                                onChange={handleInputChange}
                                            >
                                                {field.options?.map((option, idx) => (
                                                    <option key={idx} value={option.name}>
                                                        {/* EV-338。候補（機器）の名前が空なら「-」（EV-311。空の項目にしない）。 */}
                                                        {option.name || '-'}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : field.type === "multi-select" && field.search ? (
                                            <SearchCombobox<{ name: string; id: string | number }>
                                                id={`${fieldId}-${index}`}
                                                label={field.label}
                                                describedBy={field.note ? `${fieldId}-${index}-note` : undefined}
                                                multiple
                                                selected={addNewFormValue[field.label] || []}
                                                fetchPage={field.search.fetchPage}
                                                resetKey={field.search.resetKey}
                                                getKey={(option) => option.id}
                                                getLabel={(option) => option.name}
                                                onSelect={(option) => addOption(field.label, option)}
                                                onRemove={(option) => removeOption(field.label, option)}
                                            />
                                        ) : field.type === "multi-select" ? (
                                            <MultiSelect
                                                id={`${fieldId}-${index}`}
                                                label={field.label}
                                                describedBy={field.note ? `${fieldId}-${index}-note` : undefined}
                                                name={field.label}
                                                options={field.options}
                                                selected={addNewFormValue[field.label] || []}
                                                open={Boolean(showOptions[field.label])}
                                                onOpenChange={(open) => setOptionsOpen(field.label, open)}
                                                onChange={(e) => handleMultiSelectChange(e, field.label)}
                                                onRemove={(option) => removeOption(field.label, option)}
                                                placeholder={t('ui.select-options', { ns: 'common' })}
                                            />
                                        ) : null}
                                        {/* PR #253 の Codex の P2（4257906741）。注記は知らせとして読み上げ、機器の欄に結ぶ（固定の id）。 */}
                                        {field.note && (
                                            <p id={`${fieldId}-${index}-note`} role="alert" className="mt-1 text-red-700">{field.note}</p>
                                        )}
                                        {field.noteAction && (
                                            <button
                                                type="button"
                                                className="mt-1 rounded-md border px-3 py-1 text-primary/300"
                                                onClick={field.noteAction.onClick}
                                            >
                                                {field.noteAction.label}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </fieldset>
                        {unresolved ? (
                            <p id={noteId} className="px-6 pt-4 text-body-2 text-neutral/300">
                                {t('operator.unresolved-note', { ns: 'layout' })}
                            </p>
                        ) : null}
                        <div className="flex justify-between space-x-2 px-6 py-4 font-semi-bold">
                            <button type="button"
                                className="flex-1 py-2 px-4 bg-gray-300 text-primary/300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleClosePopup}
                            >
                                {t('add-driver.cancel')}
                            </button>
                            <button type="button"
                                className="flex-1 py-2 px-4 bg-primary/300 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleSaveChanges}
                                disabled={isSaving}
                                aria-busy={isSaving}
                            >
                                {unresolved ? t('operator.resend-same', { ns: 'layout' }) : save}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default AddNewPopUp;